<script setup>
/**
 * The questions a member is asked about a competition, one compact row each.
 *
 * Renders WHATEVER the API returns (useCompetitionQuestions.js): per question
 * a short label beside a switch that starts OFF (leaving it is a no), and a
 * small "More info" that opens the few lines of details under the row
 * (owner, 25 Sep 2026: the old heading, intro and bullets per question were
 * too much, worst with several stacked). It holds no copy of its own for any
 * question, which is what lets the API add one without a website release.
 *
 * Redesign (owner, 30 Oct 2026, COMPLIANCE D193), all from optional API
 * fields so an older API looks as it did: a logo by key (`logo`), the
 * question as a heading (`heading`), two always visible lines (`lead`), and a
 * status line under the switch (`onLine`/`offLine`) inside a box that turns
 * green when on. More info opens `moreInfo` when the API sends it, else
 * `details`. The first-view dialog draws the logo and heading itself when
 * a single question is waiting (showLogo/showHeading false).
 *
 * Used by the first-view dialog (CompetitionQuestionsPrompt) and inline on
 * the join confirmation (/invite/[slug]). v-model is {key: true|false}.
 */
import {ref} from 'vue';
import {questionLeadText, questionLogo, questionMoreInfo, questionStatusLine} from '~/composables/useCompetitionQuestions';

defineProps({
  questions: {type: Array, default: () => []},
  modelValue: {type: Object, default: () => ({})},
  disabled: {type: Boolean, default: false},
  showLogo: {type: Boolean, default: true},
  showHeading: {type: Boolean, default: true},
});
const emit = defineEmits(['update:modelValue']);

const set = (current, key, value) => emit('update:modelValue', {...current, [key]: value === true});

const open = ref({});
const toggleInfo = (key) => { open.value = {...open.value, [key]: !open.value[key]}; };
</script>

<template>
  <div class="cql">
    <div v-for="q in questions" :key="q.key" class="cql-q" :data-question="q.key">
      <div v-if="showLogo && questionLogo(q.logo)" class="cql-logo">
        <img :src="questionLogo(q.logo).src" :alt="questionLogo(q.logo).alt"
             :width="questionLogo(q.logo).width" :height="questionLogo(q.logo).height" class="cql-logo-img"/>
      </div>
      <div v-if="showHeading && q.heading" class="cql-heading">{{ q.heading }}</div>
      <p v-if="questionLeadText(q)" class="cql-lead">{{ questionLeadText(q) }}</p>
      <div :class="questionStatusLine(q, false) || questionStatusLine(q, true)
                     ? ['cql-choice', modelValue?.[q.key] === true ? 'cql-choice--on' : null]
                     : null">
        <div class="cql-row">
          <label class="cql-label" :for="`cq-${q.key}`">{{ q.label }}</label>
          <v-switch
              :id="`cq-${q.key}`"
              :model-value="modelValue?.[q.key] === true"
              :disabled="disabled"
              :aria-label="q.label"
              density="compact"
              hide-details
              inset
              :color="questionStatusLine(q, true) ? 'success' : 'primary'"
              class="cql-switch"
              @update:model-value="(v) => set(modelValue, q.key, v)"
          />
        </div>
        <div v-if="questionStatusLine(q, modelValue?.[q.key] === true)" class="cql-state" aria-live="polite">
          <span class="cql-dot"/>{{ questionStatusLine(q, modelValue?.[q.key] === true) }}
        </div>
      </div>
      <button v-if="questionMoreInfo(q).length" type="button" class="cql-more" :aria-expanded="open[q.key] === true"
              :data-more="q.key" @click="toggleInfo(q.key)">
        More info
        <v-icon size="16" :icon="open[q.key] ? 'mdi-chevron-up' : 'mdi-chevron-down'"/>
      </button>
      <ul v-if="open[q.key] && questionMoreInfo(q).length" class="cql-details">
        <li v-for="(line, i) in questionMoreInfo(q)" :key="i">{{ line }}</li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.cql { display: flex; flex-direction: column; }
.cql-q { padding: 8px 0; border-bottom: 1px solid rgb(var(--v-theme-borderLight)); }
.cql-q:first-child { padding-top: 0; }
.cql-q:last-child { border-bottom: 0; padding-bottom: 0; }
.cql-row { display: flex; align-items: center; gap: 12px; }
.cql-label { flex: 1; min-width: 0; font-size: 14.5px; font-weight: 600; line-height: 1.35; cursor: pointer; }
.cql-switch { flex: 0 0 auto; }
.cql-more {
  display: inline-flex; align-items: center; gap: 2px;
  /* owner, 2 Nov 2026: it sat cramped against the box above and the next row */
  padding: 6px 4px; margin-top: 6px; background: none; border: 0; border-radius: 8px;
  font-size: 13px; font-weight: 600; color: rgb(var(--v-theme-primary)); cursor: pointer;
}
/* the logo always sits on white: the Squash Levels lettering is black */
.cql-logo { background: #fff; border: 1px solid rgb(var(--v-theme-borderLight)); border-radius: 12px; height: 56px; display: flex; align-items: center; justify-content: center; margin-bottom: 10px; }
.cql-logo-img { height: 26px; width: auto; max-width: 80%; }
.cql-heading { font-size: 16px; font-weight: 700; line-height: 1.3; margin-bottom: 6px; }
.cql-lead { margin: 0 0 10px; font-size: 13.5px; line-height: 1.5; color: rgb(var(--v-theme-charcoal)); }
.cql-choice { border: 1.5px solid rgb(var(--v-theme-borderLight)); border-radius: 14px; padding: 10px 14px; transition: border-color .15s ease, background-color .15s ease; }
.cql-choice--on { border-color: rgb(var(--v-theme-success)); background: rgba(var(--v-theme-success), .08); }
.cql-state { display: flex; align-items: center; gap: 6px; margin-top: 4px; font-size: 12.5px; color: rgb(var(--v-theme-charcoal)); }
.cql-choice--on .cql-state { color: rgb(var(--v-theme-text)); }
.cql-dot { width: 7px; height: 7px; border-radius: 50%; background: rgb(var(--v-theme-textSubtle)); flex: 0 0 auto; }
.cql-choice--on .cql-dot { background: rgb(var(--v-theme-success)); }
.cql-details { margin: 6px 0 0; padding-left: 18px; display: flex; flex-direction: column; gap: 4px; font-size: 13px; line-height: 1.45; opacity: .85; }
</style>
